import type { Session, User } from '@supabase/supabase-js'
import { create } from 'zustand'
import { captureException } from '../lib/sentry'
import { supabase, supabaseEnvError } from '../lib/supabase'

type AuthStatus = 'loading' | 'anonymous' | 'authenticated' | 'error'

type AuthState = {
  status: AuthStatus
  session: Session | null
  user: User | null
  error: string | null
  initialized: boolean
  initialize: () => Promise<void>
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  clearError: () => void
}

let unsubscribeAuthListener: (() => void) | null = null

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'loading',
  session: null,
  user: null,
  error: null,
  initialized: false,

  initialize: async () => {
    if (get().initialized) return
    set({ status: 'loading', error: null })

    if (!supabase) {
      set({
        status: 'error',
        error: supabaseEnvError,
        initialized: true,
      })
      return
    }

    try {
      const { data, error } = await supabase.auth.getSession()
      if (error) throw error

      const session = data.session
      set({
        session,
        user: session?.user ?? null,
        status: session ? 'authenticated' : 'anonymous',
        initialized: true,
      })

      if (!unsubscribeAuthListener) {
        const { data: listener } = supabase.auth.onAuthStateChange(
          (_event, nextSession) => {
            set({
              session: nextSession,
              user: nextSession?.user ?? null,
              status: nextSession ? 'authenticated' : 'anonymous',
              error: null,
            })
          },
        )
        unsubscribeAuthListener = () => listener.subscription.unsubscribe()
      }
    } catch (err) {
      captureException(err, 'auth_initialize')
      const message = err instanceof Error ? err.message : 'Auth init failed'
      set({
        status: 'error',
        error: message,
        initialized: true,
      })
    }
  },

  signInWithGoogle: async () => {
    if (!supabase) {
      set({ error: supabaseEnvError })
      return
    }
    set({ error: null })
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    })
    if (error) {
      captureException(error, 'sign_in_google')
      set({ error: error.message })
    }
  },

  signOut: async () => {
    if (!supabase) {
      set({ error: supabaseEnvError })
      return
    }
    const { error } = await supabase.auth.signOut()
    if (error) {
      captureException(error, 'sign_out')
      set({ error: error.message })
    }
  },

  clearError: () => set({ error: null }),
}))
